import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import useApiMethods from "../lib/useApiMethods";
import {
  getNotificationWsUrl,
  NOTIFICATION,
  NOTIFICATION_COUNT,
  NOTIFICATION_MARK_READ,
} from "../constants/api";
import { AuthContext } from "./AuthContext";
import { showNotificationToast, isRequesterOwnApprovalAlert } from "../utils/notificationToast";

const NotificationContext = createContext(null);

const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 30000;
const PING_INTERVAL_MS = 45000;
const POLL_INTERVAL_MS = 5000;
const TOASTED_IDS_STORAGE_KEY = "hrms:toasted-notification-ids";

const loadPersistedToastIds = () => {
  try {
    const raw = sessionStorage.getItem(TOASTED_IDS_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
};

const persistToastIds = (idSet) => {
  try {
    sessionStorage.setItem(
      TOASTED_IDS_STORAGE_KEY,
      JSON.stringify(Array.from(idSet).slice(-500))
    );
  } catch {
    // ignore quota errors
  }
};

export const NotificationProvider = ({ children }) => {
  const { authRevision } = useContext(AuthContext);
  const { fetch, create } = useApiMethods();

  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [listLoaded, setListLoaded] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [recentNotification, setRecentNotification] = useState(null);

  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const mountedRef = useRef(true);
  const toastedNotificationIdsRef = useRef(loadPersistedToastIds());
  const pollTimerRef = useRef(null);
  const pollingReadyRef = useRef(false);
  const intentionalCloseRef = useRef(false);

  const fetchRef = useRef(fetch);
  const createRef = useRef(create);
  fetchRef.current = fetch;
  createRef.current = create;

  const prependNotification = useCallback((notification) => {
    if (!notification?.id) return;
    setNotifications((prev) => {
      if (prev.some((item) => item.id === notification.id)) {
        return prev;
      }
      return [notification, ...prev];
    });
  }, []);

  const isUnreadNotification = useCallback((notification) => {
    return notification.is_opened === false
      || notification.is_opened === 0
      || !notification.is_opened;
  }, []);

  const rememberNotificationSeen = useCallback((notificationId) => {
    if (!notificationId) return;
    toastedNotificationIdsRef.current.add(notificationId);
    persistToastIds(toastedNotificationIdsRef.current);
  }, []);

  /** Reserve toast slot atomically — avoids WS + poll racing on the same id. */
  const claimNotificationToast = useCallback((notification) => {
    if (!notification?.id) return false;
    if (toastedNotificationIdsRef.current.has(notification.id)) return false;
    rememberNotificationSeen(notification.id);
    return isUnreadNotification(notification);
  }, [isUnreadNotification, rememberNotificationSeen]);

  /**
   * Merge notification into bell state. Toasts only when `toast` is true
   * (WebSocket push). Poll/reconnect paths stay silent for existing rows.
   */
  const processIncomingNotification = useCallback((notification, { toast = false } = {}) => {
    if (!notification?.id) return;
    if (isRequesterOwnApprovalAlert(notification)) return;

    prependNotification(notification);
    setRecentNotification(notification);

    if (toast) {
      if (claimNotificationToast(notification)) {
        showNotificationToast(notification);
      }
    } else {
      rememberNotificationSeen(notification.id);
    }
  }, [prependNotification, claimNotificationToast, rememberNotificationSeen]);

  const mergeNotificationList = useCallback((items) => {
    setNotifications((prev) => {
      const byId = new Map(prev.map((item) => [item.id, item]));
      (items || []).forEach((item) => {
        if (item?.id) byId.set(item.id, item);
      });
      return Array.from(byId.values()).sort(
        (a, b) => new Date(b.time || 0) - new Date(a.time || 0)
      );
    });
  }, []);

  const pollForNotificationsRef = useRef(null);

  const pollForNotifications = useCallback(async () => {
    if (!pollingReadyRef.current) return;

    try {
      const countRes = await fetchRef.current(NOTIFICATION_COUNT);
      if (countRes?.status === "success") {
        setUnreadCount(Number(countRes.data) || 0);
      }

      const listRes = await fetchRef.current(NOTIFICATION);
      if (listRes?.status !== "success") return;

      const items = (listRes.data || []).filter(
        (notification) => !isRequesterOwnApprovalAlert(notification)
      );
      mergeNotificationList(items);
      items.forEach((notification) => {
        if (notification?.id) {
          rememberNotificationSeen(notification.id);
        }
      });
    } catch (error) {
      console.error("Notification poll failed:", error);
    }
  }, [mergeNotificationList, rememberNotificationSeen]);

  pollForNotificationsRef.current = pollForNotifications;

  const isWsActive = useCallback(() => {
    const state = wsRef.current?.readyState;
    return state === WebSocket.OPEN || state === WebSocket.CONNECTING;
  }, []);

  const refreshCount = useCallback(async () => {
    try {
      const response = await fetchRef.current(NOTIFICATION_COUNT);
      if (response?.status === "success") {
        setUnreadCount(Number(response.data) || 0);
      }
    } catch (error) {
      console.error("Failed to fetch notification count:", error);
    }
  }, []);

  const refreshList = useCallback(async () => {
    try {
      const response = await fetchRef.current(NOTIFICATION);
      if (response?.status === "success") {
        setNotifications(response.data || []);
        setListLoaded(true);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, []);

  const markRead = useCallback(async (notificationId) => {
    if (!notificationId) return;
    let wasUnread = false;
    setNotifications((prev) =>
      prev.map((item) => {
        if (item.id === notificationId && !item.is_opened) {
          wasUnread = true;
          return { ...item, is_opened: true };
        }
        if (item.id === notificationId) {
          return { ...item, is_opened: true };
        }
        return item;
      })
    );
    if (wasUnread) {
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    try {
      await createRef.current(`/${NOTIFICATION_MARK_READ}`, { notification_id: notificationId });
    } catch (error) {
      console.error("Failed to mark notification read:", error);
      await refreshCount();
      if (listLoaded) await refreshList();
    }
  }, [listLoaded, refreshCount, refreshList]);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((item) => ({ ...item, is_opened: true })));
    setUnreadCount(0);
    try {
      await createRef.current(`/${NOTIFICATION_MARK_READ}`, { mark_all: true });
    } catch (error) {
      console.error("Failed to mark all notifications read:", error);
      await refreshCount();
      if (listLoaded) await refreshList();
    }
  }, [listLoaded, refreshCount, refreshList]);

  const handleWsMessageRef = useRef(null);

  const handleWsMessage = useCallback((event) => {
    try {
      const payload = JSON.parse(event.data);
      const { event: eventName, data } = payload;

      if (eventName === "notification.count") {
        setUnreadCount(Number(data?.unread) || 0);
        return;
      }

      if (eventName === "notification.new") {
        if (data?.notification) {
          processIncomingNotification(data.notification, { toast: true });
        }
        if (typeof data?.unread === "number") {
          setUnreadCount(data.unread);
        } else {
          setUnreadCount((prev) => prev + 1);
        }
        return;
      }

      if (eventName === "notification.read") {
        const notificationId = data?.notification_id;
        if (notificationId) {
          setNotifications((prev) =>
            prev.map((item) =>
              item.id === notificationId ? { ...item, is_opened: true } : item
            )
          );
        }
        return;
      }

      if (eventName === "notification.read_all") {
        setNotifications((prev) => prev.map((item) => ({ ...item, is_opened: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error("Invalid WebSocket notification payload:", error);
    }
  }, [processIncomingNotification]);

  handleWsMessageRef.current = handleWsMessage;

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  }, []);

  const connectWebSocketRef = useRef(null);

  const disconnectWebSocket = useCallback((intentional = false) => {
    intentionalCloseRef.current = intentional;
    clearTimers();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
  }, [clearTimers]);

  const connectWebSocket = useCallback(() => {
    const token = sessionStorage.getItem("token");
    if (!token || !mountedRef.current) return;

    disconnectWebSocket(true);

    const ws = new WebSocket(getNotificationWsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      reconnectAttemptRef.current = 0;
      setWsConnected(true);
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ event: "ping" }));
        }
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (event) => handleWsMessageRef.current?.(event);

    ws.onclose = () => {
      if (!mountedRef.current) return;
      setWsConnected(false);
      clearTimers();
      wsRef.current = null;

      if (intentionalCloseRef.current) {
        intentionalCloseRef.current = false;
        return;
      }

      pollForNotificationsRef.current?.();

      const delay = Math.min(
        RECONNECT_BASE_MS * (2 ** reconnectAttemptRef.current),
        RECONNECT_MAX_MS
      );
      reconnectAttemptRef.current += 1;
      reconnectTimerRef.current = setTimeout(() => {
        if (mountedRef.current && sessionStorage.getItem("token")) {
          connectWebSocketRef.current?.();
        }
      }, delay);
    };

    ws.onerror = () => {
      ws.close();
    };
  }, [clearTimers, disconnectWebSocket]);

  connectWebSocketRef.current = connectWebSocket;

  const teardownNotifications = useCallback(() => {
    pollingReadyRef.current = false;
    intentionalCloseRef.current = true;
    clearTimers();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setWsConnected(false);
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, [clearTimers]);

  const loadInitialNotificationsRef = useRef(null);

  loadInitialNotificationsRef.current = async () => {
    pollingReadyRef.current = false;
    try {
      const [countRes, listRes] = await Promise.all([
        fetchRef.current(NOTIFICATION_COUNT),
        fetchRef.current(NOTIFICATION),
      ]);

      if (countRes?.status === "success") {
        setUnreadCount(Number(countRes.data) || 0);
      }

      if (listRes?.status === "success") {
        const items = (listRes.data || []).filter(
          (notification) => !isRequesterOwnApprovalAlert(notification)
        );
        setNotifications(items);
        setListLoaded(true);
        items.forEach((notification) => {
          if (notification?.id) {
            rememberNotificationSeen(notification.id);
          }
        });
      }
    } catch (error) {
      console.error("Failed to load initial notifications:", error);
    } finally {
      pollingReadyRef.current = true;
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      teardownNotifications();
    };
  }, [teardownNotifications]);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      setUnreadCount(0);
      setNotifications([]);
      setListLoaded(false);
      toastedNotificationIdsRef.current.clear();
      sessionStorage.removeItem(TOASTED_IDS_STORAGE_KEY);
      teardownNotifications();
      return undefined;
    }

    let cancelled = false;

    const initNotifications = async () => {
      await loadInitialNotificationsRef.current?.();
      if (cancelled) return;
      connectWebSocketRef.current?.();
    };

    initNotifications();

    pollTimerRef.current = setInterval(() => {
      if (isWsActive()) return;
      pollForNotificationsRef.current?.();
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      teardownNotifications();
    };
  }, [authRevision, teardownNotifications]);

  const value = useMemo(
    () => ({
      unreadCount,
      notifications,
      listLoaded,
      wsConnected,
      recentNotification,
      refreshCount,
      refreshList,
      markRead,
      markAllRead,
      setNotifications,
    }),
    [
      unreadCount,
      notifications,
      listLoaded,
      wsConnected,
      recentNotification,
      refreshCount,
      refreshList,
      markRead,
      markAllRead,
    ]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
};

export default NotificationContext;
